/**
 * VerificationPipelineStepper — horizontal stepper for profile pages.
 *
 * Renders the official 6-stage workflow (Application Submitted → Screening &
 * Reports → Verification & Validation → Additional Information Needed →
 * Decision-Ready Review) followed by the "Complete" outcome state.
 *
 * The dashboard uses the vertical-list `<VerificationPipeline>` to show counts
 * across all applicants. Profile pages (Applicant / Household) need a
 * SINGLE-applicant variant: render the workflow in a horizontal row, highlight
 * the current stage, mark earlier stages complete, dim future ones. Same
 * tones (slate/blue/violet/amber/emerald) and icons as the landing page.
 *
 * Props:
 *   - currentStageKey:  one of PIPELINE_STAGES[*].key ('submitted' .. 'complete')
 *   - title:            optional label shown above the steps (defaults to the
 *                       Verify Hub eyebrow)
 *   - subtitle:         small text on the right
 *   - testid:           data-testid root
 */
import { Check } from "lucide-react";
import {
  PIPELINE_STAGES,
  PIPELINE_DOT,
  PIPELINE_FILL,
  PIPELINE_PILL,
} from "@/lib/verifyHub";

export default function VerificationPipelineStepper({
  currentStageKey,
  title,
  subtitle,
  testid = "pipeline-stepper",
}) {
  const currentIdx = Math.max(
    0,
    PIPELINE_STAGES.findIndex((s) => s.key === currentStageKey),
  );
  const currentStage = PIPELINE_STAGES[currentIdx];

  return (
    <section
      className="bg-white rounded-xl ring-1 ring-slate-200 shadow-sm overflow-hidden"
      data-testid={testid}
    >
      <header className="flex items-center justify-between gap-3 px-4 sm:px-5 py-3 bg-slate-50 border-b border-slate-200">
        <div className="min-w-0">
          <div className="text-[10px] tracking-widest uppercase text-slate-500 font-semibold">
            {title || "Application Workflow"}
          </div>
          {subtitle && (
            <div className="text-[11px] text-slate-500 mt-0.5 truncate">{subtitle}</div>
          )}
        </div>
        <span
          className={`inline-flex items-center gap-1.5 text-[10px] tracking-widest uppercase font-semibold px-2.5 py-1 rounded-full shrink-0 ${PIPELINE_PILL[currentStage.tone]}`}
          data-testid={`${testid}-current`}
        >
          <span className={`h-1.5 w-1.5 rounded-full ${PIPELINE_DOT[currentStage.tone]}`} />
          {currentStage.label}
        </span>
      </header>

      {/* Horizontal steps. Hidden labels on small screens to stay legible; only
          dots + connectors render. Numbered tooltips would help on mobile but
          the current stage pill above already names it. */}
      <div className="px-4 sm:px-6 py-5">
        <ol className="flex items-center justify-between gap-1 sm:gap-2">
          {PIPELINE_STAGES.map((s, idx) => {
            const isDone = idx < currentIdx;
            const isCurrent = idx === currentIdx;
            const Icon = s.icon;

            // Dot styling.
            const dotBase = "h-7 w-7 sm:h-8 sm:w-8 rounded-full flex items-center justify-center shrink-0 transition-all";
            const dotCls = isCurrent
              ? `${dotBase} ${PIPELINE_DOT[s.tone]} text-white scale-110 ring-4 ring-white shadow-sm`
              : isDone
                ? `${dotBase} bg-emerald-500 text-white`
                : `${dotBase} bg-slate-100 text-slate-400 ring-1 ring-slate-200`;

            // Connector to the next step (filled green if both this & next are done/current).
            const connectorFilled = isDone || (isCurrent && idx > 0);
            return (
              <li
                key={s.key}
                className="flex-1 flex items-center min-w-0"
                data-testid={`${testid}-step-${s.key}`}
                data-state={isCurrent ? "current" : isDone ? "done" : "future"}
              >
                <div className="flex flex-col items-center min-w-0 flex-1">
                  <div className={dotCls}>
                    {isDone ? (
                      <Check className="h-3.5 w-3.5 sm:h-4 sm:w-4" strokeWidth={3} />
                    ) : (
                      <Icon className="h-3.5 w-3.5 sm:h-4 sm:w-4" strokeWidth={2} />
                    )}
                  </div>
                  <div
                    className={`mt-2 text-[10px] sm:text-[11px] leading-tight text-center max-w-[100px] sm:max-w-[120px] truncate sm:whitespace-normal ${
                      isCurrent ? "text-slate-900 font-semibold" : isDone ? "text-slate-700" : "text-slate-400"
                    }`}
                    title={s.label}
                  >
                    {s.label}
                  </div>
                </div>
                {idx < PIPELINE_STAGES.length - 1 && (
                  <div className="hidden xs:block sm:flex-1 h-0.5 mx-1 sm:mx-2 -translate-y-2 sm:-translate-y-3 self-center">
                    <div className="h-full w-full bg-slate-200 overflow-hidden rounded-full">
                      <div
                        className={`h-full ${connectorFilled ? PIPELINE_FILL.complete : "bg-transparent"} transition-all duration-500`}
                        style={{ width: connectorFilled ? "100%" : "0%" }}
                      />
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ol>
      </div>
    </section>
  );
}
